import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AppBar,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Toolbar,
  Typography,
  Button,
  useTheme
} from '@material-ui/core';
import { Close, InfoOutlined } from '@material-ui/icons';
import { format, subDays } from 'date-fns';

import api from '~/services/api';
import { formatMessage, formatNumber } from '~/i18n/translate';
import { ISA_ERROR } from '~/constants';
import { useDispatch } from 'react-redux';
import DateSinglePicker from '~/components/DateSinglePicker';

interface CurrentAccountApiItem {
  RNUM: number;
  superiorExternalCode: number;
  superiorName: string;
  userExternalCode: number;
  userName: string;
  date: string;
  historic: string;
  historicExtra: string | null;
  CODFUNC: number;
  NOMEFUNC: string;
  ROTINA: string;
  productExternalCode: number;
  productName: string;
  orderExternalNumber: number;
  clientExternalCode: number;
  clientName: string;
  actualBalance: number;
  movBalance: number;
  lastBalance: number;
  creditChange: number;
  lastBalance1: number;
  creditLimit: number;
}

interface CurrentAccountApiResponse {
  data: CurrentAccountApiItem[];
  total: number;
  totalPages: number;
}

interface CurrentAccountItem extends CurrentAccountApiItem {
  day: string;
  longDate: string;
}

interface CurrentAccountModalProps {
  open: boolean;
  onClose: () => void;
}

export const CurrentAccountModal: React.FC<CurrentAccountModalProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const today = useMemo(() => new Date(), []);
  const minDate = useMemo(() => subDays(today, 60), [today]);

  const [initialDate, setInitialDate] = useState<Date>(minDate);
  const [endDate, setEndDate] = useState<Date>(today);
  const [balances, setBalances] = useState<CurrentAccountItem[]>([]);
  const [latestBalance, setLatestBalance] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [skip, setSkip] = useState<number>(0);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const LIMIT = 20;
  const isInitialLoadRef = useRef<boolean>(false);

  const getAmountColor = (value: number) => {
    if (value > 0) return theme.palette.success.main;
    if (value < 0) return theme.palette.error.main;
    return theme.palette.text.secondary;
  };

  const loadStatement = useCallback(
    async (currentSkip: number = 0, append: boolean = false) => {
      try {
        if (!open && !append) return;

        if (initialDate > endDate) {
          setError(formatMessage('orderCreateCheckoutObservationAccountBalance'));
          setBalances([]);
          setLatestBalance(0);
          setHasMore(false);
          return;
        }

        setError(null);
        if (append) {
          setLoadingMore(true);
        } else {
          setLoading(true);
          setSkip(0);
          setBalances([]);
        }

        const start = format(initialDate, 'yyyy-MM-dd');
        const end = format(endDate, 'yyyy-MM-dd');

        const { data: response } = await api.get<CurrentAccountApiResponse>('/user/statement', {
          params: { startDate: start, endDate: end, skip: currentSkip, limit: LIMIT }
        });

        const items = response?.data || [];
        const mapped: CurrentAccountItem[] = items.map(item => {
          const baseDateString = item.date?.substring(0, 10);
          const [yearStr, monthStr, dayStr] = baseDateString ? baseDateString.split('-') : [];
          const year = Number(yearStr);
          const month = Number(monthStr);
          const day = Number(dayStr);

          const parsedDate =
            !Number.isNaN(year) && !Number.isNaN(month) && !Number.isNaN(day)
              ? new Date(year, month - 1, day)
              : new Date();

          return {
            ...item,
            day: format(parsedDate, 'dd'),
            longDate: format(parsedDate, 'dd/MM/yyyy')
          };
        });

        if (append) {
          setBalances(prev => [...prev, ...mapped]);
        } else {
          setBalances(mapped);
          const latest = mapped.length > 0 ? mapped[0].actualBalance ?? 0 : 0;
          setLatestBalance(latest);
        }

        const total = response?.total ?? 0;
        setHasMore(currentSkip + mapped.length < total);
        setSkip(currentSkip + mapped.length);
      } catch (error: any) {
        dispatch({ type: ISA_ERROR, error });
        setError(formatMessage('commonErrorMessage') || 'Erro ao carregar extrato.');
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [open, initialDate, endDate, dispatch]
  );

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      loadStatement(skip, true);
    }
  };

  useEffect(() => {
    if (open && !isInitialLoadRef.current) {
      isInitialLoadRef.current = true;
      setSkip(0);
      setHasMore(false);
      loadStatement(0, false);
    } else if (!open) {
      isInitialLoadRef.current = false;
    }
  }, [open, loadStatement]);

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} PaperComponent={Paper} fullWidth maxWidth="md">
      <DialogTitle disableTypography>
        <AppBar color="primary" position="static" elevation={0}>
          <Toolbar>
            <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
              <Typography variant="h6">{formatMessage('accountBalanceMenuStatementLabel')}</Typography>
              <IconButton edge="end" color="inherit" onClick={handleClose} aria-label="close">
                <Close />
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>
      </DialogTitle>

      <DialogContent style={{ padding: theme.spacing(3) }}>
        <Box mb={3}>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} sm={5}>
              <Typography variant="caption" color="textSecondary" gutterBottom>
                {formatMessage('orderCreateCheckoutInitialDateLabel') || 'Data inicial'}
              </Typography>
              <Box mt={1}>
                <DateSinglePicker
                  value={initialDate}
                  minDate={minDate}
                  maxDate={today}
                  onChange={date => {
                    if (!date) {
                      setInitialDate(minDate);
                      return;
                    }
                    setInitialDate(date);
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={5}>
              <Typography variant="caption" color="textSecondary" gutterBottom>
                {formatMessage('orderCreateCheckoutEndDateLabel') || 'Data final'}
              </Typography>
              <Box mt={1}>
                <DateSinglePicker
                  value={endDate}
                  minDate={minDate}
                  maxDate={today}
                  onChange={date => {
                    if (!date) {
                      setEndDate(today);
                      return;
                    }
                    setEndDate(date);
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={() => {
                  setSkip(0);
                  setHasMore(false);
                  loadStatement(0, false);
                }}
                style={{ minHeight: '36px' }}
              >
                {formatMessage('accountBalanceSearchButtonLabel') || 'Buscar'}
              </Button>
            </Grid>
          </Grid>
        </Box>

        <Box mb={2}>
          <Paper variant="outlined">
            <Box display="flex" alignItems="center" justifyContent="space-between" p={2}>
              <Box display="flex" alignItems="center">
                <InfoOutlined color="action" style={{ marginRight: theme.spacing(1) }} />
                <Typography variant="body2">
                  {formatMessage('accountBalancePreviousBalanceLabel') || 'Saldo anterior'}
                </Typography>
              </Box>
              <Typography
                variant="h6"
                style={{
                  color: getAmountColor(latestBalance),
                  fontWeight: theme.typography.fontWeightBold
                }}
              >
                {formatNumber(latestBalance, { style: 'currency' })}
              </Typography>
            </Box>
          </Paper>
        </Box>

        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" py={3}>
            <CircularProgress size={32} />
          </Box>
        )}

        {error && !loading && (
          <Box mb={2}>
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          </Box>
        )}

        {!loading && !error && balances.length === 0 && (
          <Box mt={2}>
            <Typography variant="body2" color="textSecondary">
              {formatMessage('accountBalanceEmptyMessage') ||
                'Nenhum lançamento encontrado para o período selecionado.'}
            </Typography>
          </Box>
        )}

        <Box mt={2}>
          <Grid container spacing={2}>
            {balances.map((item, index) => (
              <Grid
                item
                xs={12}
                key={`${item.date}-${item.orderExternalNumber}-${item.productExternalCode}-${item.movBalance}-${index}`}
              >
                <Paper variant="outlined">
                  <Box display="flex" p={2}>
                    <Box
                      mr={2}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      minWidth={48}
                      height={48}
                      borderRadius={24}
                      bgcolor={theme.palette.background.default}
                    >
                      <Typography variant="subtitle1" color="textPrimary">
                        {item.day}
                      </Typography>
                    </Box>

                    <Box flex={1}>
                      <Box display="flex" justifyContent="space-between" flexWrap="wrap">
                        <Typography variant="body2" color="textSecondary">
                          {item.longDate}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {formatMessage('accountBalanceOrderNumberLabel') || 'Nº pedido'}: {item.orderExternalNumber}
                        </Typography>
                      </Box>

                      <Box mt={0.5}>
                        <Typography variant="body2" color="textPrimary">
                          {item.historic}
                        </Typography>
                      </Box>

                      <Box mt={0.5}>
                        <Typography variant="body2" color="textSecondary">
                          {item.productExternalCode} - {item.productName}
                        </Typography>
                      </Box>

                      <Box mt={0.5}>
                        <Typography variant="body2" color="textSecondary">
                          {item.clientName}
                        </Typography>
                      </Box>

                      <Box mt={1} display="flex" justifyContent="space-between" flexWrap="wrap">
                        <Typography
                          variant="body1"
                          style={{
                            color: getAmountColor(item.movBalance),
                            fontWeight: theme.typography.fontWeightBold
                          }}
                        >
                          {formatNumber(item.movBalance, { style: 'currency' })}
                        </Typography>
                        <Typography
                          variant="body1"
                          style={{
                            color: theme.palette.text.primary,
                            fontWeight: theme.typography.fontWeightBold
                          }}
                        >
                          {formatNumber(item.lastBalance, { style: 'currency' })}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {hasMore && (
            <Box mt={3} display="flex" justifyContent="center">
              <Button
                variant="outlined"
                color="primary"
                onClick={handleLoadMore}
                disabled={loadingMore}
                startIcon={loadingMore ? <CircularProgress size={16} /> : null}
              >
                {loadingMore
                  ? formatMessage('accountBalanceLoadingMoreLabel') || 'Carregando...'
                  : formatMessage('accountBalanceLoadMoreLabel') || 'Carregar mais'}
              </Button>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Box display="flex" justifyContent="flex-end" width="100%" p={1}>
          <Button onClick={handleClose} color="primary">
            {formatMessage('commonButtonClose') || 'Fechar'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
